import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { API_CONFIG } from '../config/api';
import { API_ENDPOINTS } from '../config/apiEndpoints';

export interface ExcelData {
  fileName: string;
  headers: string[];
  rows: any[][];
  sheetNames: string[];
  selectedSheet: string;
}

export interface ComparisonResult {
  column: string;
  file1Value: any;
  file2Value: any;
  rowIndex: number;
  isDifferent: boolean;
  file1RowId?: string;
  file2RowId?: string;
}

export interface ComparisonSummary {
  totalRows: number;
  matchingRows: number;
  differentRows: number;
  file1OnlyRows: number;
  file2OnlyRows: number;
  columnsCompared: string[];
  discrepancies: ComparisonResult[];
}

export interface MissingRecord {
  id: string;
  source: 'offer_tracker' | 'avature_list';
  data: any[];
  headers: string[];
}

export interface MultiPartComparisonResult {
  summary: {
    totalOfferTrackerRecords: number;
    totalAvatureListRecords: number;
    matchedRecords: number;
    recordsWithDifferences: number;
    offerTrackerOnlyRecords: number;
    avatureListOnlyRecords: number;
  };
  differences: ComparisonResult[];
  missingInOfferTracker: MissingRecord[];
  missingInAvatureList: MissingRecord[];
  columnsCompared: string[];
  downloadInfo?: {
    s3_url: string;
    object_key: string;
    filename: string;
    download_available: number;
    file_size_mb: number;
  };
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const getToken = () => localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);

// Column mapping between Offer Tracker and Avature List tabs
export const COLUMN_MAPPING = {
  'Candidate ID': 'Person ID', // Key matching columns
  Offering: 'Business Line (for Financial Reporting)',
  'New Talent Group': 'Talent Group',
  'New Sub TG': 'Talent Sub-Group',
  'Type Of Hire': 'Type of Hire',
  'Employment Type': 'Offer Type',
  Gender: 'Gender',
  Source: 'Source Detail',
  'KEY_CAPBLITYDESC (USI New)': 'Key Capability',
  'Primary Skills': 'Job Title for External Postings',
  'Current Firm': 'Employer',
  'Hiring Location': 'Hiring Location',
  'Relevant Experience': 'Relevant Experience (in months)',
  'Agreed DOJ': 'Tentative Date of Joining',
  'Decline Reason': 'Indicate reason for offer decline/revoke',
  'Panel 1': 'Interview Round 1- Interviewer(s)',
  'Panel 2': 'Interview Round 2 - Interviewer(s)',
  'Final Round Panel': 'Interview Roaund 3 - Interviewer(s)',
  'Final Shortlist Date': 'Final Shortlist',
  'Offered Designation': 'Job Designation',
  YAL: 'Year at Level',
  'Offered CTC': 'Proposed Total Salary',
  'Joining Bonus': 'Joining Bonus',
  'Offer Release Data': 'Written Offer Extended',
  'FTH End Date': 'Hire End Date',
  'BPaas Hire- Yes/No?': 'BpaaS Offer',
};

export class ExcelComparisonService {
  /**
   * Parse Excel file and extract data
   */
  static async parseExcelFile(file: File): Promise<ExcelData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          const sheetNames = workbook.SheetNames;
          const firstSheet = workbook.Sheets[sheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          const headers = (jsonData[0] as string[]) || [];
          const rows = jsonData.slice(1) as any[][];

          resolve({
            fileName: file.name,
            headers,
            rows,
            sheetNames,
            selectedSheet: sheetNames[0],
          });
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse both Offer Tracker and Avature List tabs from a single Excel file
   */
  static async parseBothTabs(
    file: File
  ): Promise<{ offerTracker: ExcelData; avatureList: ExcelData }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          // Check if both required sheets exist
          if (!workbook.Sheets['Offer Tracker']) {
            reject(new Error('Sheet "Offer Tracker" not found in the Excel file'));
            return;
          }

          if (!workbook.Sheets['Avature List']) {
            reject(new Error('Sheet "Avature List" not found in the Excel file'));
            return;
          }

          // Parse Offer Tracker sheet
          const offerTrackerSheet = workbook.Sheets['Offer Tracker'];
          const offerTrackerData = XLSX.utils.sheet_to_json(offerTrackerSheet, { header: 1 });
          const offerTrackerHeaders = (offerTrackerData[0] as string[]) || [];
          const offerTrackerRows = offerTrackerData.slice(1) as any[][];

          // Parse Avature List sheet
          const avatureListSheet = workbook.Sheets['Avature List'];
          const avatureListData = XLSX.utils.sheet_to_json(avatureListSheet, { header: 1 });
          const avatureListHeaders = (avatureListData[0] as string[]) || [];
          const avatureListRows = avatureListData.slice(1) as any[][];

          resolve({
            offerTracker: {
              fileName: `${file.name} - Offer Tracker`,
              headers: offerTrackerHeaders,
              rows: offerTrackerRows,
              sheetNames: workbook.SheetNames,
              selectedSheet: 'Offer Tracker',
            },
            avatureList: {
              fileName: `${file.name} - Avature List`,
              headers: avatureListHeaders,
              rows: avatureListRows,
              sheetNames: workbook.SheetNames,
              selectedSheet: 'Avature List',
            },
          });
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse specific sheet from Excel file
   */
  static parseExcelSheet(file: File, sheetName: string): Promise<ExcelData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          if (!workbook.Sheets[sheetName]) {
            reject(new Error(`Sheet "${sheetName}" not found`));
            return;
          }

          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          const headers = (jsonData[0] as string[]) || [];
          const rows = jsonData.slice(1) as any[][];

          resolve({
            fileName: file.name,
            headers,
            rows,
            sheetNames: workbook.SheetNames,
            selectedSheet: sheetName,
          });
        } catch (error) {
          reject(new Error(`Failed to parse Excel sheet: ${error}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Compare Offer Tracker and Avature List tabs using ID-based matching
   */
  static compareTabsWithIdMatching(
    offerTracker: ExcelData,
    avatureList: ExcelData
  ): MultiPartComparisonResult {
    // Find the ID columns
    const candidateIdIndex = offerTracker.headers.indexOf('Candidate ID');
    const personIdIndex = avatureList.headers.indexOf('Person ID');

    if (candidateIdIndex === -1) {
      throw new Error('Candidate ID column not found in Offer Tracker tab');
    }

    if (personIdIndex === -1) {
      throw new Error('Person ID column not found in Avature List tab');
    }

    // Create maps for efficient lookup
    const offerTrackerMap = new Map<string, { row: any[]; index: number }>();
    const avatureListMap = new Map<string, { row: any[]; index: number }>();

    // Populate Offer Tracker map
    offerTracker.rows.forEach((row, index) => {
      const candidateId = row[candidateIdIndex];
      if (candidateId !== undefined && candidateId !== null && candidateId !== '') {
        offerTrackerMap.set(String(candidateId).trim(), { row, index });
      }
    });

    // Populate Avature List map
    avatureList.rows.forEach((row, index) => {
      const personId = row[personIdIndex];
      if (personId !== undefined && personId !== null && personId !== '') {
        avatureListMap.set(String(personId).trim(), { row, index });
      }
    });

    // Find mapped columns that exist in both sheets (excluding ID columns)
    const mappedColumns: Array<{ offerTrackerCol: string; avatureListCol: string }> = [];

    Object.entries(COLUMN_MAPPING).forEach(([offerTrackerCol, avatureListCol]) => {
      // Skip the ID columns as they're used for matching
      if (offerTrackerCol === 'Candidate ID') return;

      if (
        offerTracker.headers.includes(offerTrackerCol) &&
        avatureList.headers.includes(avatureListCol)
      ) {
        mappedColumns.push({ offerTrackerCol, avatureListCol });
      }
    });

    const differences: ComparisonResult[] = [];
    const missingInOfferTracker: MissingRecord[] = [];
    const missingInAvatureList: MissingRecord[] = [];

    let matchedRecords = 0;
    let recordsWithDifferences = 0;

    // Get all unique IDs from both datasets
    const allIds = new Set<string>();
    offerTrackerMap.forEach((_, id) => allIds.add(id));
    avatureListMap.forEach((_, id) => allIds.add(id));

    allIds.forEach(id => {
      const offerTrackerRecord = offerTrackerMap.get(id);
      const avatureListRecord = avatureListMap.get(id);

      if (!offerTrackerRecord && avatureListRecord) {
        // Record exists only in Avature List
        missingInOfferTracker.push({
          id,
          source: 'avature_list',
          data: avatureListRecord.row,
          headers: avatureList.headers,
        });
      } else if (offerTrackerRecord && !avatureListRecord) {
        // Record exists only in Offer Tracker
        missingInAvatureList.push({
          id,
          source: 'offer_tracker',
          data: offerTrackerRecord.row,
          headers: offerTracker.headers,
        });
      } else if (offerTrackerRecord && avatureListRecord) {
        // Record exists in both - compare mapped columns
        matchedRecords++;
        let hasDiscrepancy = false;

        mappedColumns.forEach(({ offerTrackerCol, avatureListCol }) => {
          const otIndex = offerTracker.headers.indexOf(offerTrackerCol);
          const alIndex = avatureList.headers.indexOf(avatureListCol);

          if (otIndex !== -1 && alIndex !== -1) {
            const otValue = offerTrackerRecord.row[otIndex];
            const alValue = avatureListRecord.row[alIndex];
            
            const normalizedOtValue = ExcelComparisonService.normalizeValue(otValue);
            const normalizedAlValue = ExcelComparisonService.normalizeValue(alValue);
            
            if (normalizedOtValue !== normalizedAlValue) {
              hasDiscrepancy = true;
              differences.push({
                column: `${offerTrackerCol} → ${avatureListCol}`,
                file1Value: otValue,
                file2Value: alValue,
                rowIndex: offerTrackerRecord.index,
                isDifferent: true,
                file1RowId: id,
                file2RowId: id,
              });
            }
          }
        });

        if (hasDiscrepancy) {
          recordsWithDifferences++;
        }
      }
    });

    return {
      summary: {
        totalOfferTrackerRecords: offerTrackerMap.size,
        totalAvatureListRecords: avatureListMap.size,
        matchedRecords,
        recordsWithDifferences,
        offerTrackerOnlyRecords: missingInAvatureList.length,
        avatureListOnlyRecords: missingInOfferTracker.length,
      },
      differences,
      missingInOfferTracker,
      missingInAvatureList,
      columnsCompared: mappedColumns.map(
        ({ offerTrackerCol, avatureListCol }) => `${offerTrackerCol} → ${avatureListCol}`
      ),
    };
  }

  /**
   * Compare Offer Tracker and Avature List tabs using column mapping
   */
  static compareTabsWithMapping(
    offerTracker: ExcelData,
    avatureList: ExcelData,
    keyColumn?: string
  ): ComparisonSummary {
    // Find mapped columns that exist in both sheets
    const mappedColumns: Array<{ offerTrackerCol: string; avatureListCol: string }> = [];

    Object.entries(COLUMN_MAPPING).forEach(([offerTrackerCol, avatureListCol]) => {
      if (
        offerTracker.headers.includes(offerTrackerCol) &&
        avatureList.headers.includes(avatureListCol)
      ) {
        mappedColumns.push({ offerTrackerCol, avatureListCol });
      }
    });

    if (mappedColumns.length === 0) {
      throw new Error('No mapped columns found between the two tabs');
    }

    const discrepancies: ComparisonResult[] = [];
    let matchingRows = 0;
    let differentRows = 0;

    // Create maps for efficient lookup if key column is specified
    const offerTrackerMap = new Map();
    const avatureListMap = new Map();

    if (keyColumn) {
      // Find the mapped column name in Avature List for the key column
      const keyColumnMapping = Object.entries(COLUMN_MAPPING).find(
        ([otCol]) => otCol === keyColumn
      );
      const avatureKeyColumn = keyColumnMapping ? keyColumnMapping[1] : keyColumn;

      if (
        offerTracker.headers.includes(keyColumn) &&
        avatureList.headers.includes(avatureKeyColumn)
      ) {
        const keyIndex1 = offerTracker.headers.indexOf(keyColumn);
        const keyIndex2 = avatureList.headers.indexOf(avatureKeyColumn);

        offerTracker.rows.forEach((row, index) => {
          const key = row[keyIndex1];
          if (key !== undefined && key !== null && key !== '') {
            offerTrackerMap.set(String(key).trim(), { row, index });
          }
        });

        avatureList.rows.forEach((row, index) => {
          const key = row[keyIndex2];
          if (key !== undefined && key !== null && key !== '') {
            avatureListMap.set(String(key).trim(), { row, index });
          }
        });
      }
    }

    // Compare rows
    if (keyColumn && offerTrackerMap.size > 0 && avatureListMap.size > 0) {
      // Key-based comparison
      const allKeys = new Set<string>();
      offerTrackerMap.forEach((_, key) => allKeys.add(key));
      avatureListMap.forEach((_, key) => allKeys.add(key));

      allKeys.forEach(key => {
        const item1 = offerTrackerMap.get(key);
        const item2 = avatureListMap.get(key);

        if (!item1 || !item2) {
          // Row exists in only one tab
          differentRows++;
          return;
        }

        let hasDiscrepancy = false;

        mappedColumns.forEach(({ offerTrackerCol, avatureListCol }) => {
          const index1 = offerTracker.headers.indexOf(offerTrackerCol);
          const index2 = avatureList.headers.indexOf(avatureListCol);

          if (index1 !== -1 && index2 !== -1) {
            const value1 = item1.row[index1];
            const value2 = item2.row[index2];
            
            const normalizedValue1 = ExcelComparisonService.normalizeValue(value1);
            const normalizedValue2 = ExcelComparisonService.normalizeValue(value2);
            
            if (normalizedValue1 !== normalizedValue2) {
              hasDiscrepancy = true;
              discrepancies.push({
                column: `${offerTrackerCol} → ${avatureListCol}`,
                file1Value: value1,
                file2Value: value2,
                rowIndex: item1.index,
                isDifferent: true,
                file1RowId: key,
                file2RowId: key,
              });
            }
          }
        });

        if (hasDiscrepancy) {
          differentRows++;
        } else {
          matchingRows++;
        }
      });
    } else {
      // Row-by-row comparison (when no key column or key column not found)
      const maxRows = Math.max(offerTracker.rows.length, avatureList.rows.length);

      for (let i = 0; i < maxRows; i++) {
        const row1 = offerTracker.rows[i];
        const row2 = avatureList.rows[i];

        if (!row1 || !row2) {
          differentRows++;
          continue;
        }

        let hasDiscrepancy = false;

        mappedColumns.forEach(({ offerTrackerCol, avatureListCol }) => {
          const index1 = offerTracker.headers.indexOf(offerTrackerCol);
          const index2 = avatureList.headers.indexOf(avatureListCol);

          if (index1 !== -1 && index2 !== -1) {
            const value1 = row1[index1];
            const value2 = row2[index2];
            
            const normalizedValue1 = ExcelComparisonService.normalizeValue(value1);
            const normalizedValue2 = ExcelComparisonService.normalizeValue(value2);
            
            if (normalizedValue1 !== normalizedValue2) {
              hasDiscrepancy = true;
              discrepancies.push({
                column: `${offerTrackerCol} → ${avatureListCol}`,
                file1Value: value1,
                file2Value: value2,
                rowIndex: i,
                isDifferent: true,
              });
            }
          }
        });

        if (hasDiscrepancy) {
          differentRows++;
        } else {
          matchingRows++;
        }
      }
    }

    let file1OnlyRows = 0;
    let file2OnlyRows = 0;

    if (keyColumn) {
      offerTrackerMap.forEach((_, key) => {
        if (!avatureListMap.has(key)) file1OnlyRows++;
      });
      avatureListMap.forEach((_, key) => {
        if (!offerTrackerMap.has(key)) file2OnlyRows++;
      });
    } else {
      file1OnlyRows = Math.max(0, offerTracker.rows.length - avatureList.rows.length);
      file2OnlyRows = Math.max(0, avatureList.rows.length - offerTracker.rows.length);
    }

    return {
      totalRows: Math.max(offerTracker.rows.length, avatureList.rows.length),
      matchingRows,
      differentRows,
      file1OnlyRows,
      file2OnlyRows,
      columnsCompared: mappedColumns.map(
        ({ offerTrackerCol, avatureListCol }) => `${offerTrackerCol} → ${avatureListCol}`
      ),
      discrepancies,
    };
  }

  /**
   * Compare two Excel datasets and identify discrepancies
   */
  static compareExcelData(
    data1: ExcelData,
    data2: ExcelData,
    keyColumn?: string
  ): ComparisonSummary {
    const commonHeaders = data1.headers.filter(header => data2.headers.includes(header));

    if (commonHeaders.length === 0) {
      throw new Error('No common columns found between the two files');
    }

    const discrepancies: ComparisonResult[] = [];
    let matchingRows = 0;
    let differentRows = 0;

    // Create maps for efficient lookup if key column is specified
    const data1Map = new Map();
    const data2Map = new Map();

    if (keyColumn && data1.headers.includes(keyColumn) && data2.headers.includes(keyColumn)) {
      const keyIndex1 = data1.headers.indexOf(keyColumn);
      const keyIndex2 = data2.headers.indexOf(keyColumn);

      data1.rows.forEach((row, index) => {
        const key = row[keyIndex1];
        if (key !== undefined && key !== null && key !== '') {
          data1Map.set(String(key).trim(), { row, index });
        }
      });

      data2.rows.forEach((row, index) => {
        const key = row[keyIndex2];
        if (key !== undefined && key !== null && key !== '') {
          data2Map.set(String(key).trim(), { row, index });
        }
      });
    }

    // Compare rows
    if (keyColumn && data1Map.size > 0 && data2Map.size > 0) {
      // Key-based comparison
      const allKeys = new Set<string>();
      data1Map.forEach((_, key) => allKeys.add(key));
      data2Map.forEach((_, key) => allKeys.add(key));

      allKeys.forEach(key => {
        const item1 = data1Map.get(key);
        const item2 = data2Map.get(key);

        if (!item1 || !item2) {
          // Row exists in only one file
          differentRows++;
          return;
        }

        let hasDiscrepancy = false;

        commonHeaders.forEach(header => {
          const index1 = data1.headers.indexOf(header);
          const index2 = data2.headers.indexOf(header);

          if (index1 !== -1 && index2 !== -1) {
            const value1 = item1.row[index1];
            const value2 = item2.row[index2];
            
            const normalizedValue1 = ExcelComparisonService.normalizeValue(value1);
            const normalizedValue2 = ExcelComparisonService.normalizeValue(value2);
            
            if (normalizedValue1 !== normalizedValue2) {
              hasDiscrepancy = true;
              discrepancies.push({
                column: header,
                file1Value: value1,
                file2Value: value2,
                rowIndex: item1.index,
                isDifferent: true,
                file1RowId: key,
                file2RowId: key,
              });
            }
          }
        });

        if (hasDiscrepancy) {
          differentRows++;
        } else {
          matchingRows++;
        }
      });
    } else {
      // Row-by-row comparison (when no key column or key column not found)
      const maxRows = Math.max(data1.rows.length, data2.rows.length);

      for (let i = 0; i < maxRows; i++) {
        const row1 = data1.rows[i];
        const row2 = data2.rows[i];

        if (!row1 || !row2) {
          differentRows++;
          continue;
        }

        let hasDiscrepancy = false;

        commonHeaders.forEach(header => {
          const index1 = data1.headers.indexOf(header);
          const index2 = data2.headers.indexOf(header);

          if (index1 !== -1 && index2 !== -1) {
            const value1 = row1[index1];
            const value2 = row2[index2];
            
            const normalizedValue1 = ExcelComparisonService.normalizeValue(value1);
            const normalizedValue2 = ExcelComparisonService.normalizeValue(value2);
            
            if (normalizedValue1 !== normalizedValue2) {
              hasDiscrepancy = true;
              discrepancies.push({
                column: header,
                file1Value: value1,
                file2Value: value2,
                rowIndex: i,
                isDifferent: true,
              });
            }
          }
        });

        if (hasDiscrepancy) {
          differentRows++;
        } else {
          matchingRows++;
        }
      }
    }

    let file1OnlyRows = 0;
    let file2OnlyRows = 0;

    if (keyColumn) {
      data1Map.forEach((_, key) => {
        if (!data2Map.has(key)) file1OnlyRows++;
      });
      data2Map.forEach((_, key) => {
        if (!data1Map.has(key)) file2OnlyRows++;
      });
    } else {
      file1OnlyRows = Math.max(0, data1.rows.length - data2.rows.length);
      file2OnlyRows = Math.max(0, data2.rows.length - data1.rows.length);
    }

    return {
      totalRows: Math.max(data1.rows.length, data2.rows.length),
      matchingRows,
      differentRows,
      file1OnlyRows,
      file2OnlyRows,
      columnsCompared: commonHeaders,
      discrepancies,
    };
  }

  /**
   * Normalize values for comparison
   */
  private static normalizeValue(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    return String(value).trim().toLowerCase();
  }

  /**
   * Export comparison results to Excel
   */
  static exportComparisonResults(
    comparison: ComparisonSummary,
    data1: ExcelData,
    data2: ExcelData
  ): void {
    const workbook = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Comparison Summary'],
      [''],
      ['Total Rows', comparison.totalRows],
      ['Matching Rows', comparison.matchingRows],
      ['Different Rows', comparison.differentRows],
      [`${data1.fileName} Only`, comparison.file1OnlyRows],
      [`${data2.fileName} Only`, comparison.file2OnlyRows],
      [''],
      ['Columns Compared', comparison.columnsCompared.join(', ')],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Discrepancies sheet
    if (comparison.discrepancies.length > 0) {
      const discrepancyHeaders = [
        'Row Index',
        'Column',
        `${data1.fileName} Value`,
        `${data2.fileName} Value`,
        'Row ID (if applicable)',
      ];

      const discrepancyData = [
        discrepancyHeaders,
        ...comparison.discrepancies.map(disc => [
          disc.rowIndex + 1,
          disc.column,
          disc.file1Value || '',
          disc.file2Value || '',
          disc.file1RowId || '',
        ]),
      ];

      const discrepancySheet = XLSX.utils.aoa_to_sheet(discrepancyData);
      XLSX.utils.book_append_sheet(workbook, discrepancySheet, 'Discrepancies');
    }

    // Generate file and save
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    saveAs(blob, `Excel_Comparison_Report_${timestamp}.xlsx`);
  }

  /**
   * Export filtered results to Excel
   */
  static exportFilteredResults(
    discrepancies: ComparisonResult[],
    fileName1: string,
    fileName2: string,
    filterColumn?: string
  ): void {
    // This will be replaced with backend export API call in the future
    const headers = ['Row Index', 'Column', `${fileName1} Value`, `${fileName2} Value`, 'Row ID'];

    const csvContent = [
      headers.join(','),
      ...discrepancies.map(disc =>
        [
          disc.rowIndex + 1,
          `"${disc.column}"`,
          `"${disc.file1Value || ''}"`,
          `"${disc.file2Value || ''}"`,
          `"${disc.file1RowId || ''}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const exportFileName = filterColumn
      ? `${filterColumn}_Discrepancies_${timestamp}.csv`
      : `Filtered_Discrepancies_${timestamp}.csv`;

    saveAs(blob, exportFileName);
  }

  static exportFullReport(comparisonResult: MultiPartComparisonResult): void {
    // This will be replaced with backend export API call in the future
    const summaryData = [
      'Comparison Summary',
      '',
      `Total Offer Tracker Records,${comparisonResult.summary.totalOfferTrackerRecords}`,
      `Total Avature List Records,${comparisonResult.summary.totalAvatureListRecords}`,
      `Matched Records,${comparisonResult.summary.matchedRecords}`,
      `Records with Differences,${comparisonResult.summary.recordsWithDifferences}`,
      `Only in Offer Tracker,${comparisonResult.summary.offerTrackerOnlyRecords}`,
      `Only in Avature List,${comparisonResult.summary.avatureListOnlyRecords}`,
      '',
      `Columns Compared,"${comparisonResult.columnsCompared.join(', ')}"`,
      '',
      'Differences',
      'Record ID,Column,Offer Tracker Value,Avature List Value',
      ...comparisonResult.differences.map(
        diff =>
          `"${diff.file1RowId}","${diff.column}","${diff.file1Value || ''}","${diff.file2Value || ''}"`
      ),
    ].join('\n');

    const blob = new Blob([summaryData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    saveAs(blob, `Excel_Comparison_Report_${timestamp}.csv`);
  }

  static async uploadExcelForComparison(file: File): Promise<MultiPartComparisonResult> {
    const token = getToken();
    // Validate file before sending
    if (!file) {
      throw new Error('No file provided');
    }

    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      throw new Error('Please upload a valid Excel file (.xlsx or .xls)');
    }

    if (file.size === 0) {
      throw new Error('File is empty');
    }

    if (file.size > 100 * 1024 * 1024) {
      // 100MB limit
      throw new Error('File size exceeds 100MB limit');
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    try {
      const token = getToken();
      // Validate file before sending
      if (!file) {
        throw new Error('No file provided');
      }
      
      if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        throw new Error('Please upload a valid Excel file (.xlsx or .xls)');
      }
      
      if (file.size === 0) {
        throw new Error('File is empty');
      }
      
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        throw new Error('File size exceeds 100MB limit');
      }

      const formData = new FormData();
      formData.append('file', file);

      console.log(`Uploading file: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
      
      // Create headers object more safely
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      headers['Accept'] = 'application/json';
      
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.REPORTS.AUDIT_TRACKER}`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error Response: ${response.status} ${response.statusText}`, errorText);
        throw new Error(
          `API request failed: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const result = await response.json();
      
      return ExcelComparisonService.transformApiResponse(result);
    } catch (error) {
      console.error('Error uploading Excel file to API:', error);

      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.'
        );
      }

      if (error instanceof Error && error.message.includes('Invalid Excel file format')) {
        throw new Error(
          'The uploaded file appears to be corrupted or is not a valid Excel file. Please try uploading the file again.'
        );
      }

      throw new Error(
        `Failed to process Excel file: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Download file from S3 using the S3 URL from comparison response
   */

  static async downloadFileFromS3(s3Url?: string, filename?: string): Promise<void> {
    console.log('downloadFileFromS3 called with:', { s3Url, filename });

    // Get S3 URL from parameter or session storage
    const urlToUse = s3Url || sessionStorage.getItem('excel_comparison_s3_url');
    const filenameToUse =
      filename ||
      sessionStorage.getItem('excel_comparison_filename') ||
      'excel_comparison_report.xlsx';

    console.log('Using S3 URL:', urlToUse);
    console.log('Using filename:', filenameToUse);

    const token = getToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!urlToUse) {
      throw new Error('No S3 URL available. Please run the comparison first.');
    }

    try {
      console.log('Downloading file from S3:', urlToUse);

      const downloadResponse = await fetch(
        `${BASE_URL}${API_ENDPOINTS.REPORTS.DOWNLOAD_FILE}?s3_url=${encodeURIComponent(urlToUse)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/octet-stream',
          },
        }
      );

      console.log('Download response status:', downloadResponse.status);

      if (!downloadResponse.ok) {
        const errorText = await downloadResponse.text().catch(() => 'Unknown error');
        throw new Error(
          `Download failed: ${downloadResponse.status} ${downloadResponse.statusText} - ${errorText}`
        );
      }

      // Get the content disposition for filename
      const contentDisposition = downloadResponse.headers.get('content-disposition') || '';
      let downloadFilename = filenameToUse;
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (fileNameMatch && fileNameMatch[1]) {
        downloadFilename = fileNameMatch[1].replace(/['"]/g, '');
      }

      // Create blob and download
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`File downloaded successfully: ${downloadFilename}`);

      // Clear session storage after successful download
      sessionStorage.removeItem('excel_comparison_s3_url');
      sessionStorage.removeItem('excel_comparison_filename');
    } catch (error) {
      console.error('Error downloading file from S3:', error);
      throw new Error(
        `Failed to download file: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  private static transformApiResponse(apiResponse: any): MultiPartComparisonResult {
    const result = apiResponse.result || {};
    const matches = result.Matches || [];
    const differences = result.Differences || [];
    const missingInAL = result['Missing in AL'] || []; // Records only in Offer Tracker
    const missingInOT = result['Missing in OT'] || []; // Records only in Avature List

    // Get counts
    const matchesCount = apiResponse.matches_count || 0;
    const differencesCount = apiResponse.differences_count || 0;
    const missingInALCount = apiResponse.missing_in_al_count || 0;
    const missingInOTCount = apiResponse.missing_in_ot_count || 0;

    // Transform differences array - use the API's difference count, don't recalculate
    const transformedDifferences: ComparisonResult[] = [];

    differences.forEach((diffRecord: any) => {
      const recordId = String(diffRecord.id);
      const offerTrackerData = diffRecord.offer_tracker || {};
      const avatureListData = diffRecord.avature_list || {};
      const apiDifferenceCount = diffRecord.differences || 0; // Use the API's count

      // Get all field keys from both records
      const allFields = new Set([
        ...Object.keys(offerTrackerData),
        ...Object.keys(avatureListData),
      ]);

      // Only process fields that actually have differences between the two records
      const actualDifferences: ComparisonResult[] = [];

      allFields.forEach(fieldKey => {
        // Skip the ID field as it's used for matching
        if (fieldKey.includes('Person ID') || fieldKey.includes('Candidate ID')) return;

        const otValue = offerTrackerData[fieldKey];
        const alValue = avatureListData[fieldKey];

        // Normalize values for comparison
        const normalizedOTValue = ExcelComparisonService.normalizeValue(otValue);
        const normalizedALValue = ExcelComparisonService.normalizeValue(alValue);
        
        // Only add if values are actually different
        if (normalizedOTValue !== normalizedALValue) {
          // Extract clean column name from the field key
          let columnName = fieldKey;
          if (fieldKey.includes(':')) {
            // Handle keys like "OT-Offering:AL-Business Line (for Financial Reporting)"
            const parts = fieldKey.split(':');
            if (parts.length === 2) {
              const otPart = parts[0].replace('OT-', '').trim();
              const alPart = parts[1].replace('AL-', '').trim();
              columnName = `${otPart} → ${alPart}`;
            }
          }

          actualDifferences.push({
            column: columnName,
            file1Value: otValue,
            file2Value: alValue,
            rowIndex: 0,
            isDifferent: true,
            file1RowId: recordId,
            file2RowId: recordId,
          });
        }
      });

      // Important: Only take the first N differences where N = apiDifferenceCount
      // This ensures we match exactly what the API says
      const limitedDifferences = actualDifferences.slice(0, apiDifferenceCount);
      transformedDifferences.push(...limitedDifferences);

      // If we have fewer actual differences than the API count, log a warning
      if (actualDifferences.length < apiDifferenceCount) {
        console.warn(
          `Record ${recordId}: API says ${apiDifferenceCount} differences, but only found ${actualDifferences.length} field differences`
        );
      }
    });

    // Transform missing records
    const transformedMissingInOfferTracker: MissingRecord[] = missingInOT.map(
      (record: any, index: number) => {
        const id =
          record['Person ID'] ||
          record['Business Area (for Financial Reporting)'] ||
          `al_only_${index}`;
        return {
          id: String(id),
          source: 'avature_list' as const,
          data: Object.values(record),
          headers: Object.keys(record),
        };
      }
    );

    const transformedMissingInAvatureList: MissingRecord[] = missingInAL.map(
      (record: any, index: number) => {
        const id = record['Candidate ID'] || record['Offering'] || `ot_only_${index}`;
        return {
          id: String(id),
          source: 'offer_tracker' as const,
          data: Object.values(record),
          headers: Object.keys(record),
        };
      }
    );

    // Calculate totals
    const totalOfferTrackerRecords = matchesCount + differencesCount + missingInALCount;
    const totalAvatureListRecords = matchesCount + differencesCount + missingInOTCount;

    // Generate columns compared list from the differences
    const columnsCompared = Array.from(new Set(transformedDifferences.map(diff => diff.column)));

    if (apiResponse.excel_file?.s3_url) {
      sessionStorage.setItem('excel_comparison_s3_url', apiResponse.excel_file.s3_url);
      sessionStorage.setItem(
        'excel_comparison_filename',
        apiResponse.excel_file.filename || 'excel_comparison_report.xlsx'
      );
      console.log('Stored S3 URL in session storage:', apiResponse.excel_file.s3_url);
    }

    return {
      summary: {
        totalOfferTrackerRecords,
        totalAvatureListRecords,
        matchedRecords: matchesCount,
        recordsWithDifferences: differencesCount,
        offerTrackerOnlyRecords: missingInALCount,
        avatureListOnlyRecords: missingInOTCount,
      },
      differences: transformedDifferences,
      missingInOfferTracker: transformedMissingInOfferTracker,
      missingInAvatureList: transformedMissingInAvatureList,
      columnsCompared,
      downloadInfo:
        apiResponse.excel_file && apiResponse.excel_file.download_available
          ? {
              s3_url: apiResponse.excel_file.s3_url,
              object_key: apiResponse.excel_file.object_key,
              filename: apiResponse.excel_file.filename,
              download_available: apiResponse.excel_file.download_available ? 1 : 0,
              file_size_mb: apiResponse.excel_file.file_size_mb,
            }
          : undefined,
    };
  }
}
